import type { EventDescription, Pricings } from './types/event'

export interface TicketCategoryQuantity {
  category: string
  amount: number
}

interface PricingsRequestParams {
  dateId: Ref<string | undefined>
  quantity: Ref<number>
  isPrivate: Ref<boolean | undefined>
  categoryQuantities?: Ref<Record<string, number>>
  type?: Ref<'EVENT' | 'GIFT_CARD'>
}

/**
 * Builds the ticket_categories array for a pricings API request.
 *
 * Rules:
 * - When multiple active categories exist, use them as-is from the selector.
 * - When exactly one category is active, sync its amount with the total quantity
 *   because the category selector is only shown for 2+ categories — single-category
 *   events track quantity via selectedTicketQuantity, not categoryQuantities.
 * - When no categories are provided or all amounts are 0, fall back to a single
 *   ADULT category with the total quantity.
 */
export const buildTicketCategories = (
  quantity: number,
  categoryQuantities?: Record<string, number>
): TicketCategoryQuantity[] => {
  let ticketCategories: TicketCategoryQuantity[] = []

  if (categoryQuantities && Object.keys(categoryQuantities).length > 0) {
    ticketCategories = Object.entries(categoryQuantities)
      .filter(([_, amount]) => amount > 0)
      .map(([category, amount]) => ({ category, amount }))
  }

  if (ticketCategories.length === 0 && quantity > 0) {
    return [{ category: 'ADULT', amount: quantity }]
  }

  if (ticketCategories.length === 1) {
    return [{ category: ticketCategories[0]!.category, amount: quantity }]
  }

  return ticketCategories
}

export const useFetchPricings = (
  eventId: Ref<string>,
  {
    // params to override the default params
    dateId,
    quantity,
    isPrivate,
    categoryQuantities,
    type,
  }: PricingsRequestParams
) => {
  const config = useRuntimeConfig()
  const { $i18n } = useNuxtApp()
  const { locale: ISOLocale } = $i18n
  const locale = convertISO2Locale(ISOLocale.value)
  const localeBCP47 = convertLocaleIntoBCP47(ISOLocale.value)

  const url = `v1/store/events/${eventId.value}/pricings`

  const ticketCategories = buildTicketCategories(
    quantity.value,
    categoryQuantities?.value
  )

  const body = {
    // don't send event_id (date id) for kit and special giftcard events
    ...(dateId.value && { event_id: dateId.value }),
    private: isPrivate.value,
    ticket_categories: ticketCategories,
    ...(type?.value === 'GIFT_CARD' ? { type: 'GIFT_CARD' } : {}),
  }

  return $fetch<Pricings>(url, {
    baseURL: config.public.apiBaseURL,
    body,
    method: 'POST',
    headers: {
      'Accept-Language': `${localeBCP47},${locale}`,
    },
  })
}

export const useEventPricings = (
  event: Ref<EventDescription | undefined>,
  {
    dateId = ref(undefined),
    quantity,
    isPrivate,
    categoryQuantities,
    type,
  }: PricingsRequestParams
) => {
  const pricings = ref<Record<string, Pricings>>({})
  const eventId = computed(() => eventGetters.getId(event.value))
  const loading = ref(true)
  const { pricingKey } = useEventPricingsGetters(pricings, {
    dateId,
    quantity,
    event,
  })

  const fetchPricings = async () => {
    // Capture the pricing key at the start of the request
    // This ensures we store prices under the correct key even if quantities change during the request
    const requestPricingKey = pricingKey.value
    loading.value = true

    try {
      const pricingsResponse = await useFetchPricings(eventId, {
        dateId,
        quantity,
        isPrivate,
        categoryQuantities,
        ...(type?.value === 'GIFT_CARD' ? { type: ref('GIFT_CARD') } : {}),
      })

      if (!pricingsResponse) {
        throw new Error('No pricings response')
      }

      // Set prices in cache under the key that was valid when request started
      // This ensures prices are stored correctly even if quantities changed during request
      pricings.value = {
        ...pricings.value,
        [requestPricingKey]: pricingsResponse,
      }

      // Wait for next tick to ensure reactivity has updated
      await nextTick()

      // Set loading to false after prices are set and reactivity has updated
      // This ensures prices are available when loadingPricings becomes false
      loading.value = false
    } catch (error) {
      loading.value = false
      console.error(error)
    }
  }

  return {
    pricings,
    fetchPricings,
    loading,
  }
}
